import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { BusinessProductLots, Lot, Product } from '@spike/product-model';
import { fetchLotsThunk, getProductThunk } from '@spike/products-action';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import { SidePanel } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import LotsList from './LotsList';
import LotForm, { LotFormHandle } from './LotForm';
import AdjustLot, { AdjustLotHandle } from './AdjustLot';
import LotHistory from './LotHistory';
import stock from '../StockSidebar/StockSidebar.module.scss';
import styles from './LotsSidebar.module.scss';

interface LotsSidebarProps {
    product: Product;
    onClose: () => void;
    onSaved: () => void;
}

type LotsView = 'list' | 'form' | 'adjust' | 'history';

export const LotsSidebar: FunctionComponent<LotsSidebarProps> = ({ product: initialProduct, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const lotFormRef = useRef<LotFormHandle>(null);
    const adjustLotRef = useRef<AdjustLotHandle>(null);
    const [view, setView] = useState<LotsView>('list');
    const [selectedLot, setSelectedLot] = useState<Lot | undefined>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(10);
    const [actionState, setActionState] = useState({ canSave: false, saving: false });

    const productFromStore = useSelector<RootState, Product | undefined>(state => state.products.product);
    const businessProductLots = useSelector<RootState, BusinessProductLots | undefined>(
        state => state.products.businessProductLots
    );
    const product = productFromStore && productFromStore.id === initialProduct.id ? productFromStore : initialProduct;
    const businessProductId = product.businessProduct?.id;
    const lots = businessProductLots?.businessProductId === businessProductId ? businessProductLots.lots : [];
    const totalRecords =
        businessProductLots?.businessProductId === businessProductId
            ? businessProductLots.pagination?.totalRecords ?? 0
            : 0;
    const detailsRequestRef = useRef(0);
    const [loadingDetails, setLoadingDetails] = useState(true);

    const handleActionStateChange = useCallback((next: { canSave: boolean; saving: boolean }) => {
        setActionState(next);
    }, []);

    const loadDetails = (nextPage = page, nextPageSize = pageSize) => {
        if (product.id == null || businessProductId == null) {
            setLoadingDetails(false);
            return;
        }

        const requestId = ++detailsRequestRef.current;
        setLoadingDetails(true);

        Promise.all([
            dispatch(getProductThunk(apiClientWrapper, product.id)),
            dispatch(fetchLotsThunk(apiClientWrapper, businessProductId, '', [], [], nextPage, nextPageSize))
        ]).finally(() => {
            if (detailsRequestRef.current === requestId) {
                setLoadingDetails(false);
            }
        });
    };

    useEffect(() => {
        loadDetails(page, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product.id, businessProductId, page, pageSize]);

    const reload = () => {
        loadDetails();
        onSaved();
    };

    const goToList = () => {
        setView('list');
        setSelectedLot(undefined);
        setActionState({ canSave: false, saving: false });
    };

    const handleSaved = () => {
        goToList();
        reload();
    };

    const handleAddLot = () => {
        setSelectedLot(undefined);
        setActionState({ canSave: true, saving: false });
        setView('form');
    };

    const handleEdit = (lot: Lot) => {
        setSelectedLot(lot);
        setView('form');
    };

    const handleAdjust = (lot: Lot) => {
        setSelectedLot(lot);
        setView('adjust');
    };

    const handleHistory = (lot: Lot) => {
        setSelectedLot(lot);
        setHistoryPage(1);
        setView('history');
    };

    const footerLabel = view === 'adjust' || selectedLot ? 'Save' : 'Add';
    const showFooter = view === 'form' || view === 'adjust';

    return (
        <SidePanel
            side="right"
            width={600}
            showCloseButton
            onClose={onClose}
        >
            <div className={stock.panel}>
                <div className={stock.body}>
                    {view === 'list' && (
                        <>
                            <div className={styles.listHeader}>
                                <div className={styles.listTitle}>
                                    <button
                                        type="button"
                                        className={stock.backButton}
                                        onClick={onClose}
                                        aria-label="Back to products"
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                    </button>
                                    <h2 className={stock.title}>Lots</h2>
                                </div>
                                <Button
                                    label="Add Lot"
                                    size="medium"
                                    variant="primary"
                                    leftIcon={faPlus}
                                    onClick={handleAddLot}
                                />
                            </div>
                            <LotsList
                                product={product}
                                lots={lots}
                                loading={loadingDetails}
                                page={page}
                                pageSize={pageSize}
                                totalRecords={totalRecords}
                                onPageChange={setPage}
                                onPageSizeChange={next => {
                                    setPage(1);
                                    setPageSize(next);
                                }}
                                onEdit={handleEdit}
                                onAdjust={handleAdjust}
                                onHistory={handleHistory}
                            />
                        </>
                    )}
                    {view === 'form' && (
                        <LotForm
                            ref={lotFormRef}
                            product={product}
                            lot={selectedLot}
                            onBack={goToList}
                            onSaved={handleSaved}
                            onStateChange={handleActionStateChange}
                        />
                    )}
                    {view === 'adjust' && selectedLot && (
                        <AdjustLot
                            ref={adjustLotRef}
                            product={product}
                            lot={selectedLot}
                            onBack={goToList}
                            onSaved={handleSaved}
                            onStateChange={handleActionStateChange}
                        />
                    )}
                    {view === 'history' && selectedLot && (
                        <LotHistory
                            product={product}
                            lot={selectedLot}
                            page={historyPage}
                            pageSize={historyPageSize}
                            onPageChange={setHistoryPage}
                            onPageSizeChange={next => {
                                setHistoryPage(1);
                                setHistoryPageSize(next);
                            }}
                            onBack={goToList}
                            onAdjust={() => handleAdjust(selectedLot)}
                        />
                    )}
                </div>
                {showFooter && (
                    <div className={stock.footer}>
                        <Button
                            label={footerLabel}
                            size="large"
                            variant="primary"
                            className={stock.saveButton}
                            fullWidth
                            onClick={() => {
                                if (view === 'form') {
                                    lotFormRef.current?.save();
                                    return;
                                }
                                adjustLotRef.current?.save();
                            }}
                            disabled={!actionState.canSave}
                            isLoading={actionState.saving}
                        />
                    </div>
                )}
            </div>
        </SidePanel>
    );
};

export default LotsSidebar;
